import React, { useCallback, useContext, useEffect, useMemo, useReducer } from 'react'
import moment from 'moment'
import qs from 'qs'
import { connect } from 'react-redux'
import { useIntl } from 'react-intl'
import { useAppDispatch } from 'hooks/reduxHooks'
import useQueryParams from 'hooks/useQueryParams'
import { pushWithRegion } from 'actions/NavigationActions'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import flightSearchWidgetStateReducer, { initialState, FlightSearchWidgetState, FlightSearchWidgetActions, FlightSearchWidgetDispatchAction } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import FlightSearchWidgetStateProvider from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateProvider'
import { DISABLED_MULTI_CITY_REGION_CODES, FlightsClassTypes, FlightsFareTypes, fareCabins, fareTypes, fareClassToUrlMap } from 'constants/flight'
import { FlightsCreditDetails } from 'components/Flights/types'
import { isFlightsCredit } from 'selectors/flightsSelectors'
import FlightSearchWidgetDesktop from './FlightSearchWidgetDesktop'
import FlightSearchWidgetMobile from './FlightSearchWidgetMobile'
import { countOccupantsForFlights } from 'lib/offer/occupancyUtils'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { fireInteractionEvent } from 'api/googleTagManager'
import { flightsFlightSearchClick } from 'analytics/eventDefinitions'
import * as Analytics from 'analytics/analytics'
import ModalProvider from 'contexts/ModalProvider'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import { getFlightSearchQueryparams, LastSearchSessionData, setFlightSessionRecentSearch } from 'lib/flights/flightUtils'
import { mapFlightSearchDataToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'
import { searchEventWithContext } from 'analytics/snowplow/events'
import RecentFlightSearchesCarousel from 'components/Flights/RecentFlightSearchesCarousel/RecentFlightSearchesCarousel'
import uuidV4 from 'lib/string/uuidV4Utils'
import GeoContext from 'contexts/geoContext'
import { type RecentFlightSearchesData } from 'hooks/Flights/useRecentFlightSearches'
import { Filter_1_0_0 } from '@luxuryescapes/contract-data-event-schemas/lib/com.luxgroup'

export interface FlightSearchWidgetSearchData extends Partial<FlightSearchWidgetState> {
  filterAirlines?: Array<string>
  lastSearch?: LastSearchSessionData
  disableCalendarPricing?: boolean
  dealId?: string
}

interface Props {
  className?: string
  searchData?: FlightSearchWidgetSearchData
  creditDetails?: FlightsCreditDetails
  isFlightsCredit: boolean
  pathname: string
  /**
   * Sets the airports to a 'read only' mode
   * Used when defaulting airport selections such as on flight deal pages
   */
  readOnlyAirports?: boolean
  showMultiCity?: boolean
  recentSearchesData?: RecentFlightSearchesData
  filterContexts?: Array<Filter_1_0_0>
  autoFocusArrival?: boolean
}

export function getFlightSearchWidgetRecentStateData(lastSearch?: LastSearchSessionData): Partial<FlightSearchWidgetState> {
  if (lastSearch?.fareType === FlightsFareTypes.MULTI_CITY) {
    return {}
  }

  const occupants = lastSearch?.occupants

  return {
    ...(lastSearch?.fareType && { fareType: fareTypes.find(f => f.value === lastSearch.fareType)! }),
    ...(lastSearch?.fareClass && { fareCabin: fareCabins.find(f => f.value === lastSearch.fareClass)! }),
    ...(occupants && { occupants }),
    ...((lastSearch?.flights?.length || 0) > 0 && {
      flights: lastSearch?.flights?.map(flight => {
        const checkinDate = flight?.departingDate ? moment(flight.departingDate) : undefined
        const checkoutDate = flight?.returningDate ? moment(flight.returningDate) : undefined
        const isValidDepartingDate = checkinDate?.isAfter(moment())

        return {
          id: uuidV4(),
          ...(checkinDate && isValidDepartingDate ? { checkinDate } : {}),
          ...(checkoutDate && isValidDepartingDate ? { checkoutDate } : {}),
          ...(flight?.departureAirport && { departureAirport: flight.departureAirport }),
          ...(flight?.arrivalAirport && { arrivalAirport: flight.arrivalAirport }),
        }
      }),
    }),
  }
}

function FlightSearchWidget(props: Props) {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const {
    searchData,
    isFlightsCredit,
    creditDetails,
    className,
    pathname,
    readOnlyAirports,
    showMultiCity,
    recentSearchesData,
    filterContexts,
    autoFocusArrival,
  } = props

  const loadPrefilledData = !pathname.includes('flights-search-results')

  const query = useQueryParams()
  const departingQuotedPrice = query.get('departingQuotedPrice')
  const returningQuotedPrice = query.get('returningQuotedPrice')
  const { currentRegionCode } = useContext(GeoContext)

  const recentStateData = loadPrefilledData ? getFlightSearchWidgetRecentStateData(searchData?.lastSearch) : {}
  // Preserve prefilled departure airports from searchData when merging with recent search data
  const hasPrefilledDeparture = searchData?.flights?.some(f => f.departureAirport)

  const [state, stateDispatch] = useReducer<FlightSearchWidgetState, [FlightSearchWidgetDispatchAction]>(flightSearchWidgetStateReducer, {
    ...initialState,
    ...searchData,
    ...recentStateData,
    // Re-apply searchData.flights if it has prefilled departure (to override recentStateData.flights)
    ...(hasPrefilledDeparture && searchData?.flights ? { flights: searchData.flights } : {}),
  })

  useEffect(() => {
    if (searchData) {
      const recentData = loadPrefilledData ? getFlightSearchWidgetRecentStateData(searchData?.lastSearch) : {}
      const hasPrefilledDep = searchData?.flights?.some(f => f.departureAirport)
      stateDispatch({
        type: FlightSearchWidgetActions.UPDATE_FLIGHT_SEARCH_WIDGET_DATA,
        payload: {
          ...state,
          ...searchData,
          ...recentData,
          // Re-apply searchData.flights if it has prefilled departure (to override recentData.flights)
          ...(hasPrefilledDep && searchData?.flights ? { flights: searchData.flights } : {}),
        },
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchData, loadPrefilledData])

  const {
    flights,
    occupants,
    fareType,
    fareCabin,
    flightDealId,
  } = state

  const proceedWithSearch = useCallback(() => {
    const transformedOccupants = countOccupantsForFlights([occupants])
    const isReturnFare = fareType?.value === FlightsFareTypes.RETURN
    let nextFareType = fareType?.value as FlightsFareTypes

    // If there is only one multi city flight it's one way
    if (nextFareType === FlightsFareTypes.MULTI_CITY && flights.length === 1) {
      nextFareType = FlightsFareTypes.ONE_WAY
    }

    flights.forEach(flight => {
      const departDate = moment(flight.checkinDate).format(ISO_DATE_FORMAT)
      const returnDate = isReturnFare ? moment(flight.checkoutDate).format(ISO_DATE_FORMAT) : undefined

      Analytics.trackEvent(searchEventWithContext({
        searchEvent: { type: 'search_bar' },
        sortContext: { value: 'recommended' },
        filterContexts,
        searchContext:
        mapFlightSearchDataToSnowplowSearchEvent({
          searchVerticals: new Set(['flights']),
          originAirportCode: flight.departureAirport?.airportCode,
          originAirportName: flight.departureAirport?.airportName,
          destinationAirportCode: flight.arrivalAirport?.airportCode,
          destinationAirportName: flight.arrivalAirport?.airportName,
          departDate,
          returnDate,
          occupancies: transformedOccupants,
          fareClass: fareCabin?.value,
          fareType: nextFareType,
        },
        ),
      }))
    })

    fireInteractionEvent(flightsFlightSearchClick())

    setFlightSessionRecentSearch({
      fareType: nextFareType,
      fareClass: fareCabin?.value,
      occupants,
      flights: flights.map(flight => {
        const departDate = moment(flight.checkinDate).format(ISO_DATE_FORMAT)
        const returnDate = isReturnFare ? moment(flight.checkoutDate).format(ISO_DATE_FORMAT) : null

        return {
          departingDate: departDate,
          returningDate: returnDate,
          departureAirport: flight.departureAirport,
          arrivalAirport: flight.arrivalAirport,
        }
      }),
    })

    const airportsChanged = flights.some((flight, index) => {
      const isDifferentDepartureAirport = flight.departureAirport !== searchData?.flights?.[index]?.departureAirport
      const isDifferentArrivalAirport = flight.arrivalAirport !== searchData?.flights?.[index]?.arrivalAirport
      return isDifferentDepartureAirport || isDifferentArrivalAirport
    })

    let dealId = searchData?.dealId ?? flightDealId
    if (flightDealId && airportsChanged) {
      dealId = undefined
      stateDispatch({ type: FlightSearchWidgetActions.RESET_DEAL_ID })
    }

    const params = {
      adults: transformedOccupants.adults,
      children: transformedOccupants.children,
      infants: transformedOccupants.infants,
      childrenAge: transformedOccupants.childrenAge.join(','),
      fareClass: fareCabin?.value ? fareClassToUrlMap[fareCabin.value] : undefined,
      fareType: nextFareType,
      searchId: moment().unix(),
      flightsCredit: isFlightsCredit || undefined,
      carrier: isFlightsCredit ? creditDetails?.carrier : undefined,
      dealId,
      ...(!airportsChanged ? { filterAirlines: searchData?.filterAirlines?.join(',') } : {}),
    }

    const flightParams = getFlightSearchQueryparams(flights, nextFareType)

    if (nextFareType === FlightsFareTypes.MULTI_CITY) {
      dispatch(pushWithRegion('/flights-search-results-multi-city', qs.stringify({
        ...params,
        ...flightParams,
      })))

      return
    }

    dispatch(pushWithRegion('/flights-search-results', qs.stringify({
      ...params,
      ...flightParams,
      ...(fareCabin?.value === FlightsClassTypes.ECONOMY ? {
        departingQuotedPrice: airportsChanged ? flightParams.departingQuotedPrice : flightParams.departingQuotedPrice ?? departingQuotedPrice,
        returningQuotedPrice: airportsChanged ? flightParams.returningQuotedPrice : flightParams.returningQuotedPrice ?? returningQuotedPrice,
      } : {
        departingQuotedPrice: 0,
        returningQuotedPrice: 0,
      }),
    })))
  }, [
    occupants,
    fareType?.value,
    flights,
    fareCabin,
    isFlightsCredit,
    creditDetails?.carrier,
    searchData,
    dispatch,
    departingQuotedPrice,
    returningQuotedPrice,
    flightDealId,
    filterContexts,
  ])

  const onSearchAirport = useCallback(async(e?: any) => {
    e?.preventDefault()
    proceedWithSearch()
  }, [proceedWithSearch])

  const isMultiCityRegionEnabled = useMemo(() => {
    return !DISABLED_MULTI_CITY_REGION_CODES.includes(currentRegionCode)
  }, [currentRegionCode])

  const multiCityEnabled = showMultiCity && isMultiCityRegionEnabled

  return (
    <FlightSearchWidgetStateProvider state={state} dispatch={stateDispatch}>
      <ModalProvider>
        <div className={className}>
          <CSSBreakpoint min="tablet">
            <VerticalSpacer gap={16}>
              <FlightSearchWidgetDesktop
                onSearchAirport={onSearchAirport}
                readOnlyAirports={readOnlyAirports}
                showMultiCity={multiCityEnabled}
                autoFocusArrival={autoFocusArrival}
              />
              {recentSearchesData && recentSearchesData.shouldShowRecentSearches && (
                <RecentFlightSearchesCarousel
                  recentSearches={recentSearchesData.data}
                  status={recentSearchesData.status}
                />
              )}
            </VerticalSpacer>
          </CSSBreakpoint>
          <CSSBreakpoint max="mobile">
            <VerticalSpacer gap={12} as="form" onSubmit={onSearchAirport}>
              <FlightSearchWidgetMobile
                readOnlyAirports={readOnlyAirports}
                showMultiCity={multiCityEnabled}
                recentSearchesData={recentSearchesData}
              />
              <TextButton kind="primary" size="large" type="submit" fit="full-width">
                {intl.formatMessage({
                  id: '1jao5QhasS',
                  defaultMessage: 'Search',
                  description: 'Search button text for flight search widget',
                })}
              </TextButton>
            </VerticalSpacer>
          </CSSBreakpoint>
        </div>
      </ModalProvider>
    </FlightSearchWidgetStateProvider>
  )
}

function mapStateToProps(state: App.State) {
  return {
    creditDetails: state.flights.creditDetails,
    isFlightsCredit: isFlightsCredit(state),
    pathname: state.router.location.pathname,
  }
}

export default connect(mapStateToProps)(FlightSearchWidget)
